import type { Camera, Object3D } from 'three'
import { Box3, Mesh, Vector3 } from 'three'

export type ShareCardScreenRect = {
    left: number
    top: number
    right: number
    bottom: number
}

type ShareCardBoundsSource = {
    getRoot: () => Object3D | null
}

const EXCLUDED_MESH_NAMES = new Set(['Card_BG', 'Void'])

// Background peek beyond the card edges in the exported image
export const SHARE_CARD_CROP_PADDING_LEFT = 0.05
export const SHARE_CARD_CROP_PADDING_BOTTOM = 0.05

let boundsSource: ShareCardBoundsSource | null = null

const boundsBox = new Box3()
const projectedCorner = new Vector3()

export function registerShareCardBoundsSource(
    source: ShareCardBoundsSource | null,
): void {
    boundsSource = source
}

export function computeCardContentScreenRect(
    root: Object3D,
    camera: Camera,
    width: number,
    height: number,
): ShareCardScreenRect | null {
    boundsBox.makeEmpty()
    root.updateWorldMatrix(true, true)
    root.traverse((child) => {
        if (
            child instanceof Mesh &&
            child.visible &&
            !EXCLUDED_MESH_NAMES.has(child.name)
        ) {
            boundsBox.expandByObject(child)
        }
    })

    if (boundsBox.isEmpty()) {
        return null
    }

    const xs = [boundsBox.min.x, boundsBox.max.x]
    const ys = [boundsBox.min.y, boundsBox.max.y]
    const zs = [boundsBox.min.z, boundsBox.max.z]

    let minX = Infinity
    let minY = Infinity
    let maxX = -Infinity
    let maxY = -Infinity

    for (const x of xs) {
        for (const y of ys) {
            for (const z of zs) {
                projectedCorner.set(x, y, z).project(camera)
                const sx = (projectedCorner.x * 0.5 + 0.5) * width
                const sy = (-projectedCorner.y * 0.5 + 0.5) * height
                minX = Math.min(minX, sx)
                minY = Math.min(minY, sy)
                maxX = Math.max(maxX, sx)
                maxY = Math.max(maxY, sy)
            }
        }
    }

    if (!Number.isFinite(minX) || !Number.isFinite(minY)) {
        return null
    }

    return {
        left: minX,
        top: minY,
        right: maxX,
        bottom: maxY,
    }
}

export function expandShareCardCropRect(
    bounds: ShareCardScreenRect,
    viewportWidth: number,
    viewportHeight: number,
): ShareCardScreenRect {
    const cardWidth = bounds.right - bounds.left
    const cardHeight = bounds.bottom - bounds.top

    const left = Math.max(
        0,
        Math.floor(bounds.left - cardWidth * SHARE_CARD_CROP_PADDING_LEFT),
    )
    const top = Math.max(0, Math.floor(bounds.top))
    const right = Math.min(viewportWidth, Math.ceil(bounds.right))
    const bottom = Math.min(
        viewportHeight,
        Math.ceil(bounds.bottom + cardHeight * SHARE_CARD_CROP_PADDING_BOTTOM),
    )

    return { left, top, right, bottom }
}

export function resolveShareCardCropRect(
    camera: Camera,
    viewportWidth: number,
    viewportHeight: number,
): ShareCardScreenRect | null {
    const root = boundsSource?.getRoot()
    if (!root || viewportWidth <= 0 || viewportHeight <= 0) {
        return null
    }

    const contentBounds = computeCardContentScreenRect(
        root,
        camera,
        viewportWidth,
        viewportHeight,
    )
    if (!contentBounds) {
        return null
    }

    const crop = expandShareCardCropRect(
        contentBounds,
        viewportWidth,
        viewportHeight,
    )
    const width = crop.right - crop.left
    const height = crop.bottom - crop.top
    if (width <= 0 || height <= 0) {
        return null
    }

    return crop
}

export function scaleShareCardCropRect(
    crop: ShareCardScreenRect,
    scaleX: number,
    scaleY: number,
): ShareCardScreenRect {
    return {
        left: crop.left * scaleX,
        top: crop.top * scaleY,
        right: crop.right * scaleX,
        bottom: crop.bottom * scaleY,
    }
}
